/**
 * JSON:API Serializer utility
 * Handles transformation from flat objects to JSON:API format
 * Follows JSON:API v1.1 specification
 */

/**
 * Convert camelCase or snake_case to kebab-case
 */
export function toKebabCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/_/g, '-')
    .toLowerCase();
}

/**
 * Convert kebab-case to camelCase
 */
export function toCamelCase(str: string): string {
  // Handle both kebab-case (with hyphens) and snake_case (with underscores)
  return str.replace(/[-_]([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Convert object keys to kebab-case recursively
 */
export function keysToKebab(obj: Record<string, unknown>): Record<string, unknown> | unknown[] {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => keysToKebab(item as Record<string, unknown>));
  }
  
  const result: Record<string, unknown> = {};
  Object.keys(obj).forEach(key => {
    const kebabKey = toKebabCase(key);
    const value = obj[key];
    
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[kebabKey] = keysToKebab(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      result[kebabKey] = value.map(item => 
        typeof item === 'object' ? keysToKebab(item as Record<string, unknown>) : item
      );
    } else {
      result[kebabKey] = value;
    }
  });
  
  return result;
}

/**
 * Convert object keys from kebab-case to camelCase recursively
 */
export function keysToCamel(obj: Record<string, unknown>): Record<string, unknown> | unknown[] {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => keysToCamel(item as Record<string, unknown>));
  }
  
  const result: Record<string, unknown> = {};
  Object.keys(obj).forEach(key => {
    const camelKey = toCamelCase(key);
    const value = obj[key];
    
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[camelKey] = keysToCamel(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      result[camelKey] = value.map(item => 
        typeof item === 'object' ? keysToCamel(item as Record<string, unknown>) : item
      );
    } else {
      result[camelKey] = value;
    }
  });
  
  return result;
}

/**
 * Serialize data to JSON:API format
 */
export function serializeToJsonApi(
  type: string,
  data: Record<string, unknown>,
  id?: string
): Record<string, unknown> {
  const { id: dataId, ...attributes } = data;
  
  const result: Record<string, unknown> = {
    data: {
      type,
      attributes: keysToKebab(attributes)
    }
  };
  
  if (id || dataId) {
    (result.data as Record<string, unknown>).id = id || dataId;
  }
  
  return result;
}

/**
 * Deserialize JSON:API attributes to camelCase
 */
export function deserializeFromJsonApi(data: Record<string, unknown>): Record<string, unknown> {
  if (!data) return data;
  
  // Handle both single resource and array of resources
  if (data.data) {
    if (Array.isArray(data.data)) {
      return {
        ...data,
        data: data.data.map(item => deserializeSingleResource(item as Record<string, unknown>))
      };
    } else {
      return {
        ...data,
        data: deserializeSingleResource(data.data as Record<string, unknown>)
      };
    }
  }
  
  return data;
}

/**
 * Deserialize a single JSON:API resource
 */
function deserializeSingleResource(resource: Record<string, unknown>): Record<string, unknown> {
  if (!resource || !resource.attributes) return resource;
  
  console.log('Deserializing resource:', resource);
  
  const attributes = keysToCamel(resource.attributes as Record<string, unknown>);
  const result: Record<string, unknown> = {
    id: resource.id,
    ...attributes
  };
  
  // Special handling for one-time plans with lifetime access
  if (resource.type === 'one-time-plans' && result.isLifetime === true) {
    result.accessDurationMonths = 'unlimited';
    console.log('Converted lifetime plan to unlimited access duration');
  }
  
  // Handle relationships if present
  if (resource.relationships) {
    const relationships = resource.relationships as Record<string, unknown>;
    console.log('Processing relationships:', relationships);
    
    // Process each relationship
    Object.keys(relationships).forEach(key => {
      const relationship = relationships[key];
      const camelKey = toCamelCase(key);
      console.log(`Processing relationship ${key} -> ${camelKey}:`, relationship);
      
      // Check if this is a JSON:API relationship with { data: ... } structure
      if (relationship && typeof relationship === 'object' && 'data' in relationship) {
        const relationshipData = (relationship as Record<string, unknown>).data;
        
        if (Array.isArray(relationshipData)) {
          // Array of data (like prices)
          result[camelKey + 'Array'] = relationshipData.map(item => 
            typeof item === 'object' ? keysToCamel(item as Record<string, unknown>) : item
          );
          console.log(`Set ${camelKey}Array as array:`, result[camelKey + 'Array']);
        } else if (relationshipData && typeof relationshipData === 'object') {
          // Single object data
          result[camelKey] = keysToCamel(relationshipData as Record<string, unknown>);
          console.log(`Set ${camelKey} as object:`, result[camelKey]);
        } else {
          // Primitive or null data
          result[camelKey] = relationshipData;
          console.log(`Set ${camelKey} as primitive:`, result[camelKey]);
        }
      } else if (Array.isArray(relationship)) {
        // Direct array (backward compatibility)
        result[camelKey] = relationship.map(item => 
          typeof item === 'object' ? keysToCamel(item as Record<string, unknown>) : item
        );
        console.log(`Set ${camelKey} as direct array:`, result[camelKey]);
      } else if (relationship && typeof relationship === 'object') {
        // Direct object (backward compatibility)
        result[camelKey] = keysToCamel(relationship as Record<string, unknown>);
        console.log(`Set ${camelKey} as direct object:`, result[camelKey]);
      } else {
        // Primitive or null
        result[camelKey] = relationship;
        console.log(`Set ${camelKey} as primitive:`, result[camelKey]);
      }
    });
  }
  
  console.log('Final deserialized result:', result);
  return result;
}